import React, { useRef, useState } from 'react';
import { DateTime } from 'luxon';
import HoneyPot, { IHoneyPot } from '../../Shared/HoneyPot';
import { LanguageService } from '../../../services/language.service';
import ValidationInfo from '../../../validation/validation-info';
import TextFieldFormGroup, { ITextFieldFormGroupState } from '../../Shared/TextFieldFormGroup';
import DatePicker, { IDatePickerState } from '../../Shared/DatePicker';
import RadioFormGroup, { IRadioGroupFormGroupState } from '../../Shared/RadioFormGroup';
import LogService from '../../../services/log-service/log.service';
import AntiforgeryService from '../../../services/anti-forgery.service';
import { DateHelper } from '../../../helpers/date.helper';
import { IOrderTicketInspectionFormApiModel } from './models/order-ticket-inspection-form.api-model';
import Alert from '../../Shared/Alert';
import IValidatable, { IServerValidationError } from '../../Shared/validatable.interface';
import { ValidatableComponent, ValidationHelper } from '../../../helpers/validation.helper';
import { AnalyticsService } from '../../../services/analytics-service';
import ValidationSummary from '../../Shared/ValidationSummary';
import { DomHelper } from '../../../helpers/dom.helper';
import { IMultiFacetSelector, IMultiFacetSelectorState } from '../../Shared/MultiFacetSelector';
import MultiFacetSelector from '../../Shared/MultiFacetSelector';
import { MultiFacetType } from '../../../models/multi-facet-type.enum';
import LocationAutocomplete, { type ILocationAutocompleteState } from '../../Shared/LocationAutocomplete';
import { LocationType } from '../../../models/location-type.enum';
import { AutocompleteLabelPosition } from '../../Shared/AutocompleteBase/autocomplete-base.enum';
import { IAutocomplete } from '../../Shared/AutocompleteBase';
import ContainerItemBlockHeading from '../../Shared/ContainerItemBlockHeading';
import { IFormBaseSettings } from '../form-base-props.interface';
import { useTexts } from '../../../hooks/texts.hook';
import { IServerSideRendered } from '../../Shared/Base/server-side-rendered';

export interface IOrderTicketInspectionFormTexts {
  heading: string;
  submit: string;
  submitted: string;
  formsRequiredFieldsMessage: string;
}

export type IOrderTicketInspectionFormSettings = IFormBaseSettings;

type IOrderTicketInspectionFormProps = IServerSideRendered<IOrderTicketInspectionFormTexts, IOrderTicketInspectionFormSettings>;

function OrderTicketInspectionForm({ settings, texts }: IOrderTicketInspectionFormProps) {
  // === HOOKS ===
  const { t } = useTexts();

  // === STATE ===
  const [trafficCompanyState, setTrafficCompanyState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [orderDateState, setOrderDateState] = useState<IDatePickerState>({ value: DateTime.now().toFormat('yyyy-MM-dd'), validationInfo: new ValidationInfo() });
  const [contactPersonCompanyState, setContactPersonCompanyState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [roleFunctionState, setRoleFunctionState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [phoneState, setPhoneState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [emailState, setEmailState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [contactPersonVasttrafikState, setContactPersonVasttrafikState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [linesState, setLinesState] = useState<IMultiFacetSelectorState>({ value: [], validationInfo: new ValidationInfo() });
  const [startStopAreaState, setStartStopAreaState] = useState<ILocationAutocompleteState>({ value: undefined, validationInfo: new ValidationInfo() });
  const [departureTimeState, setDepartureTimeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [desiredTimeOfDayState, setDesiredTimeOfDayState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [controlFrequencyPeriodState, setControlFrequencyPeriodState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [checkEntireLineState, setCheckEntireLineState] = useState<IRadioGroupFormGroupState>({ value: 'true', validationInfo: new ValidationInfo() });
  const [perceivedSituationState, setPerceivedSituationState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [otherState, setOtherState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [show, setShow] = useState(true);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  // === REFS ===
  const honeyPotRef = useRef<IHoneyPot>(null!);
  const trafficCompanyRef = useRef<IValidatable>(null!);
  const orderDateRef = useRef<IValidatable>(null!);
  const contactPersonCompanyRef = useRef<IValidatable>(null!);
  const roleFunctionRef = useRef<IValidatable>(null!);
  const phoneRef = useRef<IValidatable>(null!);
  const emailRef = useRef<IValidatable>(null!);
  const contactPersonVasttrafikRef = useRef<IValidatable>(null!);
  const linesRef = useRef<IMultiFacetSelector>(null!);
  const startStopAreaRef = useRef<IAutocomplete & IValidatable>(null!);
  const departureTimeRef = useRef<IValidatable>(null!);
  const desiredTimeOfDayRef = useRef<IValidatable>(null!);
  const controlFrequencyPeriodRef = useRef<IValidatable>(null!);
  const perceivedSituationRef = useRef<IValidatable>(null!);
  const otherRef = useRef<IValidatable>(null!);

  // === VARIABLES ===
  const isRequiredFieldsValid =
    !!trafficCompanyState.value &&
    !!orderDateState.value &&
    !!contactPersonCompanyState.value &&
    !!roleFunctionState.value &&
    !!emailState.value &&
    !!contactPersonVasttrafikState.value &&
    !!linesState.value &&
    linesState.value.length > 0 &&
    !!startStopAreaState.value?.location?.name &&
    !!desiredTimeOfDayState.value &&
    !!perceivedSituationState.value;
  const showSubmitButton = settings.alwaysShowSubmitButton || isRequiredFieldsValid;
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === HELPER FUNCTIONS ===
  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: trafficCompanyRef.current, validationInfo: trafficCompanyState.validationInfo, setState: setTrafficCompanyState },
      { component: contactPersonCompanyRef.current, validationInfo: contactPersonCompanyState.validationInfo, setState: setContactPersonCompanyState },
      { component: roleFunctionRef.current, validationInfo: roleFunctionState.validationInfo, setState: setRoleFunctionState },
      { component: phoneRef.current, validationInfo: phoneState.validationInfo, setState: setPhoneState },
      { component: emailRef.current, validationInfo: emailState.validationInfo, setState: setEmailState },
      { component: contactPersonVasttrafikRef.current, validationInfo: contactPersonVasttrafikState.validationInfo, setState: setContactPersonVasttrafikState },
      { component: linesRef.current, validationInfo: linesState.validationInfo, setState: setLinesState },
      { component: startStopAreaRef.current, validationInfo: startStopAreaState.validationInfo, setState: setStartStopAreaState },
      { component: departureTimeRef.current, validationInfo: departureTimeState.validationInfo, setState: setDepartureTimeState },
      { component: desiredTimeOfDayRef.current, validationInfo: desiredTimeOfDayState.validationInfo, setState: setDesiredTimeOfDayState },
      { component: controlFrequencyPeriodRef.current, validationInfo: controlFrequencyPeriodState.validationInfo, setState: setControlFrequencyPeriodState },
      { component: perceivedSituationRef.current, validationInfo: perceivedSituationState.validationInfo, setState: setPerceivedSituationState },
      { component: otherRef.current, validationInfo: otherState.validationInfo, setState: setOtherState },
    ];
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.Forms, AnalyticsService.ErrorAction, `Beställning av biljettkontroll - ${e}`);
    });
    if (invalidValidationInfos.length > 0) {
      setTimeout(() => {
        (document.getElementsByClassName('validation-summary')[0] as HTMLElement)?.scrollIntoView();
        DomHelper.focusOnFirstFocusableElement(document.getElementsByClassName('order-ticket-inspection-form')[0] as HTMLElement);
      }, 100);
    }
  }

  // === EVENT HANDLERS ===
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting || !honeyPotRef.current.isValid()) return;

    const [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
    if (!isFormValid) {
      ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
        AnalyticsService.addEvent(AnalyticsService.Forms, AnalyticsService.ErrorAction, `Beställning av biljettkontroll - ${e}`);
      });
      return;
    }

    setIsSubmitting(true);
    setShowErrorMessage(false);

    try {
      const requestData: IOrderTicketInspectionFormApiModel = {
        $type: 'OrderTicketInspection',
        blockContentGuid: settings.blockContentGuid,
        pageContentGuid: settings.pageContentGuid,
        trafficCompany: trafficCompanyState.value,
        orderDate: orderDateState.value,
        contactPersonCompany: contactPersonCompanyState.value,
        roleFunction: roleFunctionState.value,
        phone: phoneState.value,
        email: emailState.value,
        contactPersonVasttrafik: contactPersonVasttrafikState.value,
        lines: linesState.value
          .filter((x) => x.type === MultiFacetType.Line)
          .map((x) => `${x.line?.technicalLineNumber} : ${x.line?.shortName} ${x.line?.directionName}`)
          .join(','),
        startStopArea: startStopAreaState.value?.location?.name ?? '',
        departureTime: departureTimeState.value,
        desiredTimeOfDay: desiredTimeOfDayState.value,
        controlFrequencyPeriod: controlFrequencyPeriodState.value,
        checkEntireLine: checkEntireLineState.value === 'true',
        perceivedSituation: perceivedSituationState.value,
        other: otherState.value,
      };

      const formData = new FormData();
      formData.append('feedbackForm', JSON.stringify(requestData));

      const response = await fetch(`/api/forms/submit?language=${LanguageService.getCurrentLanguage()}`, {
        method: 'POST',
        headers: { 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 400) {
          handleServerValidationErrors((await response.json()).errors);
          setTimeout(() => {
            document.getElementById('order-ticket-inspection-form-validation-summary')?.focus();
          }, 100);
          return;
        } else {
          throw new Error('Failed to submit order ticket inspection form');
        }
      }

      setIsSubmitted(true);
    } catch (error) {
      LogService.logError(error);
      setShowErrorMessage(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className={`order-ticket-inspection-form ${settings.useBorder ? 'border p-4 rounded' : ''} position-relative ${settings.colorTheme} ${show ? '' : 'd-none'} ${settings.className ?? ''}`}>
      {settings.useCloseButton && <button type="button" className="btn-close position-absolute top-0 end-0" aria-label={t('Stäng', 'Close')} onClick={() => setShow(false)} />}
      <div aria-live="polite" role="status">
        {isSubmitted && <p className="mb-0">{texts.submitted}</p>}
      </div>
      {!isSubmitted && (
        <>
          <ContainerItemBlockHeading
            headingText={texts.heading}
            headingLevel={settings.headingLevel}
            headingLevelClass={settings.headingLevelClass}
            headingVisuallyHidden={settings.headingVisuallyHidden}
          />
          <div className="mb-3" role="alert" aria-live="polite">
            {texts.formsRequiredFieldsMessage}
          </div>
          <form onSubmit={handleSubmit}>
            <ValidationSummary id="order-ticket-inspection-form-validation-summary" validationSummaryErrors={validationSummaryErrors} className={validationSummaryErrors.length > 0 ? 'mb-3' : ''} />
            <div className="mb-3">
              <TextFieldFormGroup
                ref={trafficCompanyRef}
                serverValidationName="trafficCompany"
                labelText="Trafikföretag"
                helpTextElement={<span>Ange vilket trafikföretag du arbetar på.</span>}
                inputId={`trafficCompany-${settings.blockContentGuid}`}
                autocomplete="off"
                maxLength={200}
                required={true}
                state={trafficCompanyState}
                onChange={(value) => {
                  setTrafficCompanyState({ ...trafficCompanyState, value });
                }}
              />
            </div>

            <div className="mb-3">
              <DatePicker
                ref={orderDateRef}
                serverValidationName="orderDate"
                labelText="Datum för beställning"
                minDate={DateTime.now().toFormat(DateHelper.DATE_FORMAT)}
                maxDate={DateTime.now().plus({ days: 30 }).toFormat(DateHelper.DATE_FORMAT)}
                showAsteriskOnRequiredFields={true}
                state={orderDateState}
                onChange={(value) => {
                  setOrderDateState({ ...orderDateState, value, validationInfo: new ValidationInfo() });
                }}
              />
            </div>

            <div className="mb-3">
              <TextFieldFormGroup
                ref={contactPersonCompanyRef}
                serverValidationName="contactPersonCompany"
                labelText="Kontaktperson på trafikföretaget"
                helpTextElement={<span>Ange vem vi kan kontakta angående beställningen.</span>}
                inputId={`contactPersonCompany-${settings.blockContentGuid}`}
                autocomplete="off"
                maxLength={200}
                required={true}
                state={contactPersonCompanyState}
                onChange={(value) => {
                  setContactPersonCompanyState({ ...contactPersonCompanyState, value });
                }}
              />
            </div>

            <div className="mb-3">
              <TextFieldFormGroup
                ref={roleFunctionRef}
                serverValidationName="roleFunction"
                labelText="Roll/funktion"
                helpTextElement={<span>Ange kontaktpersonens yrkesroll, t.ex. depåchef, teamledare, verksamhetsutvecklare etc.</span>}
                inputId={`roleFunction-${settings.blockContentGuid}`}
                autocomplete="off"
                maxLength={200}
                required={true}
                state={roleFunctionState}
                onChange={(value) => {
                  setRoleFunctionState({ ...roleFunctionState, value });
                }}
              />
            </div>

            <div className="mb-3">
              <TextFieldFormGroup
                ref={phoneRef}
                serverValidationName="phone"
                labelText="Telefonnummer"
                helpTextElement={<span>Ange telefonnummer till kontaktpersonen.</span>}
                inputId={`phone-${settings.blockContentGuid}`}
                type="text"
                inputMode="tel"
                autocomplete="off"
                maxLength={20}
                state={phoneState}
                onChange={(value) => {
                  setPhoneState({ ...phoneState, value });
                }}
              />
            </div>

            <div className="mb-3">
              <TextFieldFormGroup
                ref={emailRef}
                serverValidationName="email"
                labelText="E-post"
                helpTextElement={<span>Ange e-post till kontaktpersonen.</span>}
                inputId={`email-${settings.blockContentGuid}`}
                inputMode="email"
                autocomplete="email"
                maxLength={100}
                required={true}
                state={emailState}
                onChange={(value) => {
                  setEmailState({ ...emailState, value });
                }}
              />
            </div>

            <div className="mb-3">
              <TextFieldFormGroup
                ref={contactPersonVasttrafikRef}
                serverValidationName="contactPersonVasttrafik"
                labelText="Kontaktperson på Västtrafik"
                helpTextElement={<span>Ange ert företags affärskontakt på Västtrafik.</span>}
                inputId={`contactPersonVasttrafik-${settings.blockContentGuid}`}
                autocomplete="off"
                maxLength={200}
                required={true}
                state={contactPersonVasttrafikState}
                onChange={(value) => {
                  setContactPersonVasttrafikState({ ...contactPersonVasttrafikState, value });
                }}
              />
            </div>

            <div className="mb-3">
              <MultiFacetSelector
                id="multi-facet-selector"
                ref={linesRef}
                serverValidationName="lines"
                labelText="Linjer"
                multiFacetTypes={[MultiFacetType.Line]}
                required={true}
                noResultsText={t('Inga sökresultat', 'No search results')}
                onChange={(value) => setLinesState({ value, validationInfo: new ValidationInfo() })}
                state={linesState}
                placeholderText=""
              />
            </div>

            <div className="mb-3">
              <LocationAutocomplete
                ref={startStopAreaRef}
                id={`startStopArea-${settings.blockContentGuid}`}
                serverValidationName="startStopArea"
                labelText="Starthållplats"
                labelClassName="form-label"
                labelPosition={AutocompleteLabelPosition.above}
                locationTypes={[LocationType.stopArea]}
                enableCurrentPosition={false}
                required={true}
                state={startStopAreaState}
                onSelect={(value) => {
                  setStartStopAreaState({ value, validationInfo: new ValidationInfo() });
                }}
              />
            </div>

            <div className="mb-3">
              <TextFieldFormGroup
                ref={departureTimeRef}
                serverValidationName="departureTime"
                labelText="Tid för avgång"
                inputId={`departureTime-${settings.blockContentGuid}`}
                autocomplete="off"
                maxLength={50}
                state={departureTimeState}
                onChange={(value) => {
                  setDepartureTimeState({ ...departureTimeState, value });
                }}
              />
            </div>

            <div className="mb-3">
              <TextFieldFormGroup
                ref={desiredTimeOfDayRef}
                serverValidationName="desiredTimeOfDay"
                labelText="Önskad tid på dygnet"
                helpTextElement={<span>Exempelvis 07:00-11:30, högtrafik morgon/kväll, etc.</span>}
                inputId={`desiredTimeOfDay-${settings.blockContentGuid}`}
                autocomplete="off"
                maxLength={200}
                required={true}
                state={desiredTimeOfDayState}
                onChange={(value) => {
                  setDesiredTimeOfDayState({ ...desiredTimeOfDayState, value });
                }}
              />
            </div>

            <div className="mb-3">
              <TextFieldFormGroup
                ref={controlFrequencyPeriodRef}
                serverValidationName="controlFrequencyPeriod"
                labelText="Kontrollfrekvens och period (önskemål)"
                inputId={`controlFrequencyPeriod-${settings.blockContentGuid}`}
                autocomplete="off"
                maxLength={500}
                state={controlFrequencyPeriodState}
                onChange={(value) => {
                  setControlFrequencyPeriodState({ ...controlFrequencyPeriodState, value });
                }}
              />
            </div>

            <div className="mb-3">
              <RadioFormGroup
                id={`checkEntireLine-${settings.blockContentGuid}`}
                labelText="Kontrollera hela linjen"
                rowLayout={true}
                options={[
                  { value: 'true', text: 'Ja' },
                  { value: 'false', text: 'Nej' },
                ]}
                state={checkEntireLineState}
                onChange={(value) => {
                  setCheckEntireLineState({ ...checkEntireLineState, value, validationInfo: new ValidationInfo() });
                }}
              />
            </div>

            <div className="mb-3">
              <TextFieldFormGroup
                ref={perceivedSituationRef}
                serverValidationName="perceivedSituation"
                labelText="Upplevd situation"
                helpTextElement={<span>Beskriv varför ni önskar biljettkontroll.</span>}
                inputId={`perceivedSituation-${settings.blockContentGuid}`}
                autocomplete="off"
                renderAsTextArea={true}
                autoAdjustTextAreaHeight={true}
                rows={3}
                maxLength={2000}
                required={true}
                state={perceivedSituationState}
                onChange={(value) => {
                  setPerceivedSituationState({ ...perceivedSituationState, value });
                }}
              />
            </div>

            <div className="mb-3">
              <TextFieldFormGroup
                ref={otherRef}
                serverValidationName="other"
                labelText="Övrigt"
                inputId={`other-${settings.blockContentGuid}`}
                autocomplete="off"
                renderAsTextArea={true}
                autoAdjustTextAreaHeight={true}
                rows={3}
                maxLength={2000}
                required={false}
                state={otherState}
                onChange={(value) => {
                  setOtherState({ ...otherState, value });
                }}
              />
            </div>
            <Alert show={showErrorMessage} heading={t.generalError} closable={false} className="mb-3" />
            <HoneyPot ref={honeyPotRef} />
            {showSubmitButton && (
              <button type="submit" className="btn btn-primary w-100" disabled={isSubmitting}>
                {texts.submit}
              </button>
            )}
          </form>
        </>
      )}
    </div>
  );
}

export default OrderTicketInspectionForm;